import { useState, useCallback } from 'react';
import { message } from 'antd';
import { outboundNoticeApi, FM140, FM141, M206CreateRequest } from '../../../services/outboundNoticeApi';

export type Mode = 'browse' | 'add' | 'edit';

/** Doc type labels */
export const DOC_TYPE_LABELS: Record<string, string> = {
  A: 'P/O退貨',
  C: '庫位移轉',
  D: '客訴買收交運單',
  E: '客戶寄庫交運單',
};

/** Voucher type labels */
export const VOUCHER_TYPE_LABELS: Record<number, string> = {
  0: '(無)',
  1: '折讓四聯單',
  2: '開發票',
};

export function useOutboundNoticeForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [record, setRecord] = useState<FM140 | null>(null);
  const [formData, setFormData] = useState<Partial<FM140>>({});
  const [details, setDetails] = useState<Partial<FM141>[]>([]);
  const [loading, setLoading] = useState(false);
  const [allIds, setAllIds] = useState<string[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);

  const isEditing = mode !== 'browse';

  /** Load the full list of IDs */
  const loadList = useCallback(async (): Promise<string[]> => {
    const res = await outboundNoticeApi.list(1, 10000);
    const ids = (res.data || []).map(r => r.fm140_01).filter(Boolean);
    setAllIds(ids);
    return ids;
  }, []);

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await outboundNoticeApi.get(id);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details || []);
    } catch {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FM140, value: FM140[keyof FM140]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FM140): FM140[keyof FM140] => {
    return (formData as Record<string, FM140[keyof FM140]>)[key] ?? '';
  }, [formData]);

  /** Build API request from form state */
  const buildRequest = useCallback((): M206CreateRequest => {
    return {
      fm140_11: (formData.fm140_11 as string) || null,
      fm140_12: (formData.fm140_12 as string) || 'A',
      fm140_13: (formData.fm140_13 as string) || '',
      fm140_15: (formData.fm140_15 as string) || '',
      fm140_16: (formData.fm140_16 as string) || '',
      fm140_17: (formData.fm140_17 as string) || null,
      fm140_18: (formData.fm140_18 as string) || '',
      fm140_20: (formData.fm140_20 as string) || '',
      fm140_21: (formData.fm140_21 as number) || 0,
      fm140_22: (formData.fm140_22 as string) || 'NTD',
      fm140_23: (formData.fm140_23 as string) || '',
      fm140_24: (formData.fm140_24 as boolean) || false,
      fm140_91: (formData.fm140_91 as string) || '',
      details: details as FM141[],
    };
  }, [formData, details]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    saveMode: Mode,
    onSuccess: (result: FM140) => void,
  ) => {
    setLoading(true);
    try {
      const req = buildRequest();
      if (saveMode === 'add') {
        const result = await outboundNoticeApi.create(req);
        message.success(`新增成功: ${result.fm140_01}`);
        onSuccess(result);
      } else {
        const result = await outboundNoticeApi.update(formData.fm140_01!, req);
        message.success('修改成功');
        onSuccess(result);
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : '儲存失敗';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  }, [formData, buildRequest]);

  /** Delete current record */
  const handleDelete = useCallback(async (onSuccess: () => void) => {
    if (!record?.fm140_01) return;
    setLoading(true);
    try {
      await outboundNoticeApi.delete(record.fm140_01);
      message.success('刪除成功');
      onSuccess();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [record]);

  /** Revert form data to last loaded record */
  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record]);

  /** Reset form for add mode */
  const resetForAdd = useCallback(() => {
    const today = new Date().toISOString().substring(0, 10);
    setRecord(null);
    setFormData({
      fm140_12: 'A',
      fm140_22: 'NTD',
      fm140_80: '',
      fm140_11: today,
      fm140_17: today,
    });
    setDetails([]);
  }, []);

  /** Apply result after save */
  const applyResult = useCallback((result: FM140) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  /** Add a detail row */
  const addDetailRow = useCallback(() => {
    setDetails(prev => [
      ...prev,
      {
        fm141_02: prev.length + 1,
        fm141_11: '',
        fm141_12: '',
        fm141_121: '',
        fm141_13: '',
        fm141_14: '',
        fm141_15: 0,
        fm141_16: 0,
        fm141_17: 0,
        fm141_20: 1,
        fm141_21: '',
        fm141_221: '',
        fm141_222: '',
        fm141_23: 0,
      },
    ]);
  }, []);

  /** Delete a detail row by index */
  const deleteDetailRow = useCallback((idx: number) => {
    setDetails(prev => prev.filter((_, i) => i !== idx));
  }, []);

  /** Navigate to a specific index */
  const navTo = useCallback(async (idx: number) => {
    if (idx < 0 || idx >= allIds.length || isEditing) return;
    setCurrentIdx(idx);
    await loadRecord(allIds[idx]);
  }, [allIds, isEditing, loadRecord]);

  return {
    mode, setMode, isEditing,
    record, formData, details, loading,
    allIds, currentIdx, setCurrentIdx,
    setDetails,
    loadList, loadRecord, navTo,
    setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow,
  };
}
